import * as XLSX from "xlsx";
import { formatDate, formatDateTime12, formatDateTime24 } from "@/utils/helpers";

function patientHeaderRows(patient: Record<string, unknown> | null | undefined, deviceData: unknown[] | undefined): (string | number)[][] {
  if (!patient || typeof patient !== "object") {
    return [["Patient name", "—"], ["DOB", "—"], []];
  }
  const p = patient as Record<string, unknown>;
  const name = [p.firstName, p.middleName, p.lastName]
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).trim())
    .join(" ");

  const device : any = Array.isArray(deviceData) ? deviceData?.length > 0 ? deviceData[0] : {} : {};


  const dob =
    p.dateOfBirth != null && String(p.dateOfBirth).trim() !== ""
      ? formatDate(String(p.dateOfBirth))
      : "—";
  return [
    ["Patient name", name || "—"],
    ["DOB", dob],
    // ["Serial Number", device?.serialNumber ?? "—"],
    // ["Device Name", device?.deviceName ?? "—"],
    // ["IMEI", device?.imei ?? "—"],
    [],
  ];
}

function readingDateDisplay(r: Record<string, unknown>): string {
  const raw = (r.readingDateLocal ?? r.readingDate) as string | undefined;
  if (!raw) return "";
  return formatDateTime12(raw);
}

function identifierFromList(reading: any, type: "imei" | "serial" | "iccid") {
  const list = reading?.rawData?.payload?.device?.deviceIdentifiers;
  const match = Array.isArray(list) ? list.find((item) => item?.type === type) : undefined;
  const value = match?.value;
  return value != null && String(value).trim() !== "" ? String(value) : "";
}

function readingDeviceName(reading: any) {
  return String(reading?.deviceName ?? reading?.rawData?.deviceName ?? reading?.deviceId?.deviceName ?? "");
}

function readingDeviceId(reading: any) {
  const directImei = reading?.imei != null ? String(reading.imei).trim() : "";
  const deviceImei = reading?.deviceId?.imei != null ? String(reading.deviceId.imei).trim() : "";
  return (
    directImei ||
    identifierFromList(reading, "imei") ||
    identifierFromList(reading, "serial") ||
    identifierFromList(reading, "iccid") ||
    deviceImei
  );
}

function sortReadingsNewestFirst(readings: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...readings].sort((a, b) => {
    const ta = new Date(String(a.readingDateLocal ?? a.readingDate ?? 0)).getTime();
    const tb = new Date(String(b.readingDateLocal ?? b.readingDate ?? 0)).getTime();
    return tb - ta;
  });
}

export type DeviceTabExportKey =
  | "device"
  | "blood_pressure"
  | "blood_glucose"
  | "pulse_oximeter"
  | "weight_scale"
  | "pulse_rate";

/**
 * Single-sheet XLSX for the active Device tab (list or one reading type).
 */
export function exportPatientDeviceTabXlsx(
  patient: Record<string, unknown> | null | undefined,
  tabKey: DeviceTabExportKey,
  tabLabel: string,
  devices: unknown[] | undefined,
  readings: unknown[] | undefined,
  patientId: string,
  activeTab?: string,
): void {
  // console.log("devices", devices);
  let deviceType = activeTab === "blood_glucose" ? "glucose_meter" : activeTab;
  let deviceData = devices?.filter((device : any) => { if(deviceType === (device?.deviceType)) {
    return device; 
  }});

  // console.log("activeTab", activeTab);
  // console.log("devices", devices);
  // console.log("deviceData", deviceData);
  const header = patientHeaderRows(patient, deviceData);
  const titleRow: (string | number)[][] = [[`${tabLabel} — export`], []];

  let tableHeader: string[] = [];
  const dataRows: (string | number)[][] = [];

  if (tabKey === "device") {
    tableHeader = [
      "Device Name",
      "Device Type",
      "Manufacturer",
      "Model",
      "Serial Number",
      "Status",
      "Assigned At",
    ];
    const list = Array.isArray(devices) ? devices : [];
    for (const item of list) {
      const d = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
      const assignedRaw = d.assignedAt;
      const assignedAt =
        assignedRaw != null && String(assignedRaw).trim() !== ""
          ? formatDateTime12(String(assignedRaw))
          : "";
      const dtype = d.deviceType != null ? String(d.deviceType).replace(/_/g, " ") : "";
      const statusRaw = d.status != null ? String(d.status) : "";
      const status = statusRaw.replace(/([A-Z])/g, " $1").trim();
      dataRows.push([
        String(d.deviceName ?? ""),
        dtype,
        String(d.manufacturer ?? ""),
        String(d.model ?? ""),
        String(d.serialNumber ?? ""),
        status,
        assignedAt,
      ]);
    }
  } else if (tabKey === "blood_pressure") {
    tableHeader = ["Date", "BP (Systolic/Diastolic)", "Pulse","Device Name" , "IMEI"];
    const list = sortReadingsNewestFirst(
      (Array.isArray(readings) ? readings : []) as Record<string, unknown>[],
    );
    for (const r of list as any[]) {
      const sys = r.systolic;
      const dia = r.diastolic;
      const bp =
        sys != null && dia != null ? `${sys}/${dia}` : sys != null ? String(sys) : "";
      dataRows.push([readingDateDisplay(r), bp, r.pulse != null ? String(r.pulse) : "", readingDeviceName(r), readingDeviceId(r)]);
    }
  } else if (tabKey === "blood_glucose") {
    tableHeader = ["Date", "Glucose","Device Name" , "IMEI"];
    const list = sortReadingsNewestFirst(
      (Array.isArray(readings) ? readings : []) as Record<string, unknown>[],
    );
    for (const r of list as any[]) {
      const g = r.glucose;
      const unit = r.glucoseUnit != null ? String(r.glucoseUnit) : "mg/dl";
      const cell = g != null ? `${g} ${unit}` : "";
      dataRows.push([readingDateDisplay(r), cell, readingDeviceName(r), readingDeviceId(r)]);
    }
  } else if (tabKey === "pulse_oximeter") {
    tableHeader = ["Date", "SpO2 (%)", "Pulse (BPM)","Device Name" , "IMEI"];
    const list = sortReadingsNewestFirst(
      (Array.isArray(readings) ? readings : []) as Record<string, unknown>[],
    );
    for (const r of list as any[]) {
      const spo2 = r.spo2 != null ? `${r.spo2}%` : "";
      const pulse = r.pulse != null ? String(r.pulse) : "";
      dataRows.push([readingDateDisplay(r), spo2, pulse, readingDeviceName(r), readingDeviceId(r)]);
    }
  } else if (tabKey === "weight_scale") {
    tableHeader = ["Date", "Weight","Device Name" , "IMEI"];
    const list = sortReadingsNewestFirst(
      (Array.isArray(readings) ? readings : []) as Record<string, unknown>[],
    );
    for (const r of list as any[]) {
      const w = r.weight;
      const unit = r.weightUnit != null ? String(r.weightUnit) : "lbs";
      const cell =
        w !== undefined && w !== null && !Number.isNaN(Number(w))
          ? `${Number(w).toFixed(2)} ${unit}`
          : "";
      dataRows.push([readingDateDisplay(r), cell, readingDeviceName(r), readingDeviceId(r)]);
    }
  } else if (tabKey === "pulse_rate") {
    tableHeader = ["Date", "Pulse rate (BPM)","Device Name" , "IMEI"];
    const list = sortReadingsNewestFirst(
      (Array.isArray(readings) ? readings : []) as Record<string, unknown>[],
    );
    for (const r of list as any[]) {
      const pulse = r.pulse !== undefined && r.pulse !== null ? `${r.pulse} BPM` : "";
      dataRows.push([readingDateDisplay(r), pulse, readingDeviceName(r), readingDeviceId(r)]);
    }
  }

  const aoa: (string | number)[][] = [
    ...header,
    ...titleRow,
    tableHeader,
    ...dataRows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  const sheet = tabLabel.replace(/[^\w.-]+/g, "_").slice(0, 28) || "export";
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  const safeId = String(patientId).replace(/[^\w.-]+/g, "_");
  const safeTab = String(tabKey).replace(/[^\w.-]+/g, "_");
  XLSX.writeFile(wb, `${safeTab}-${safeId}.xlsx`);
}
